// Safety Walks module — shared types
// Mirrors the backend entities (SafetyWalk, SafetyObservation, SafetyWalkSettings).

export type SafetyWalkStatus = 'Draft' | 'In Progress' | 'Completed';
export type ObservationStatus = 'Open' | 'In Progress' | 'Pending Verification' | 'Closed';
export type RiskRating = 'Low' | 'Medium' | 'High' | 'Critical';

export const RISK_RATINGS: RiskRating[] = ['Low', 'Medium', 'High', 'Critical'];

export const OBSERVATION_CATEGORIES: string[] = [
    'Housekeeping',
    'PPE',
    'Working At Heights',
    'Scaffold',
    'Electrical',
    'Plant & Equipment',
    'Traffic Management',
    'Environmental',
    'Chemical Safety',
    'Other',
];

export interface ParticipantRef {
    id: number | null;
    name: string;
}

export interface AuditEntry {
    id: string;
    action: string;
    who: string;
    date: string;
    details?: Record<string, unknown> | null;
}

export interface ActivityComment {
    id: string;
    authorName: string;
    authorId: number | null;
    text: string;
    date: string;
    type: 'comment' | 'system';
}

export interface PhotoMeta {
    id: string;
    filename: string;
    key: string;
    url: string;
    size: number;
    mimetype: string;
    uploadedBy: number | null;
    uploadedAt: string;
}

export interface SafetyWalk {
    id: number;
    walkNumber: string;
    status: SafetyWalkStatus;
    project?: string | null;
    location?: string | null;
    walkDate?: string | null;
    conductedBy?: string | null;
    conductedById?: number | null;
    participants: ParticipantRef[];
    generalComments?: string | null;
    audit: AuditEntry[];
    observations?: SafetyObservation[];
    observationCounts?: { total: number; open: number; closed: number };
    createdAt: string;
    updatedAt: string;
}

export interface SafetyObservation {
    id: number;
    obsNumber: string;
    walkId: number;
    walk?: SafetyWalk | null;
    title?: string | null;
    category?: string | null;
    description?: string | null;
    riskRating: RiskRating;
    status: ObservationStatus;
    personResponsibleId?: number | null;
    personResponsibleName?: string | null;
    company?: string | null;
    dueDate?: string | null;
    beforePhoto?: PhotoMeta[] | null;
    afterPhoto?: PhotoMeta[] | null;
    correctiveAction?: string | null;
    resolutionNotes?: string | null;
    fixedDate?: string | null;
    verifiedById?: number | null;
    verifiedByName?: string | null;
    comments: ActivityComment[];
    audit: AuditEntry[];
    isOverdue?: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface WorkflowStep {
    key: string;
    label: string;
    description: string;
    status: 'Complete' | 'In Progress' | 'Pending';
    detail?: string | null;
}

export interface WalkWorkflow {
    steps: WorkflowStep[];
    completed: number;
    total: number;
    percent: number;
}

export interface WalkListResponse {
    success: boolean;
    walks: SafetyWalk[];
    total: number;
}

export interface WalkDetailResponse {
    success: boolean;
    walk: SafetyWalk;
    workflow?: WalkWorkflow;
}

export interface ObservationListResponse {
    success: boolean;
    observations: SafetyObservation[];
    total: number;
}

export interface MyTasksResponse {
    success: boolean;
    rows: SafetyObservation[];
    total: number;
    kpis: { assigned: number; open: number; inProgress: number; overdue: number };
}

export interface WalkStats {
    kpis: {
        openObservations: number;
        overdueObservations: number;
        dueThisWeek: number;
        walksThisMonth: number;
        walksLastMonth: number;
        avgCloseOutDays: number | null;
        highRisk: number;
    };
    charts: {
        byStatus: Record<ObservationStatus, number>;
        monthlyTrend: { month: string; observations: number; closed: number }[];
        byProject: { project: string; count: number }[];
        byResponsiblePerson: { name: string; count: number }[];
    };
    overdueList: {
        id: number;
        obsNumber: string;
        title?: string | null;
        riskRating: RiskRating;
        dueDate?: string | null;
        personResponsibleName?: string | null;
        project?: string | null;
    }[];
}

export interface SafetyWalkSettings {
    id: number;
    lists: {
        categories?: string[];
        riskRatings?: RiskRating[];
        [key: string]: unknown;
    };
    notificationUserIds: string[];
}

export const RISK_BADGE_CLASS: Record<RiskRating, string> = {
    Low: 'bg-slate-100 text-slate-700 border-slate-200',
    Medium: 'bg-amber-50 text-amber-700 border-amber-200',
    High: 'bg-orange-50 text-orange-700 border-orange-200',
    Critical: 'bg-red-50 text-red-700 border-red-200',
};

export const STATUS_BADGE_CLASS: Record<ObservationStatus | SafetyWalkStatus, string> = {
    Open: 'bg-blue-50 text-blue-700 border-blue-200',
    'In Progress': 'bg-amber-50 text-amber-700 border-amber-200',
    'Pending Verification': 'bg-violet-50 text-violet-700 border-violet-200',
    Closed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Draft: 'bg-slate-100 text-slate-600 border-slate-200',
    Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
